import { useState } from "react";
import type { BookingLink } from "@app/hooks/use-event-invitation-data";
import "./currency-selector.css";

type CurrencyOption = {
  label: string;
  value: string;
};

const CURRENCY_LABELS: Record<string, string> = {
  USD: "US Dollar",
  EUR: "Euro",
  GBP: "British Pound",
};

/**
 * Resolves a booking link's stored value to a safe http(s) URL. Some links are
 * stored as markdown (`[label](https://...)`); otherwise the raw value is used.
 * Returns `null` when the value is not a valid http/https URL so callers never
 * navigate to unsafe schemes (e.g. `javascript:` / `data:`).
 */
function normalizeExternalLink(value: string): string | null {
  const candidate = value.match(/\]\((https?:\/\/[^)]+)\)$/)?.[1] ?? value;

  try {
    const url = new URL(candidate);
    return url.protocol === "https:" || url.protocol === "http:"
      ? url.href
      : null;
  } catch {
    return null;
  }
}

export type CurrencySelectorProps = {
  bookingLinks: BookingLink[];
};

/**
 * Currency picker + GO action used in the invitation hero on both the public
 * landing page and the edit page. Selecting a currency and pressing GO opens
 * the matching oneworld booking link in a new tab. When no booking links exist
 * yet (e.g. while editing a draft event) it falls back to the known currency
 * labels so the control still renders.
 */
export function CurrencySelector({ bookingLinks }: CurrencySelectorProps) {
  const [selectedCurrency, setSelectedCurrency] = useState("");

  const currencyOptions: CurrencyOption[] = Array.from(
    new Set(bookingLinks.map((link) => link.currency)),
  ).map((currency) => ({
    value: currency,
    label: `${currency} - ${CURRENCY_LABELS[currency] ?? currency}`,
  }));

  const availableCurrencyOptions =
    currencyOptions.length > 0
      ? currencyOptions
      : Object.entries(CURRENCY_LABELS).map(([currency, label]) => ({
          value: currency,
          label: `${currency} - ${label}`,
        }));

  function handleGoClick() {
    if (!selectedCurrency) {
      return;
    }

    const selectedBookingLink = bookingLinks.find(
      (bookingLink) => bookingLink.currency === selectedCurrency,
    );

    if (!selectedBookingLink?.eventLink) {
      return;
    }

    const destinationUrl = normalizeExternalLink(selectedBookingLink.eventLink);
    if (!destinationUrl) {
      return;
    }

    window.open(destinationUrl, "_blank", "noopener,noreferrer");
  }

  return (
    <div className="events-invitation-hero__actions">
      <div className="events-invitation-hero__currency-select">
        <div className="events-invitation-hero__currency-select-field">
          <select
            aria-label="Choose currency"
            className="events-invitation-hero__currency-native"
            onChange={(event) => setSelectedCurrency(event.target.value)}
            value={selectedCurrency}
          >
            <option value="">Choose currency</option>
            {availableCurrencyOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <button
          className="events-invitation-hero__currency-go"
          type="button"
          onClick={handleGoClick}
        >
          GO
        </button>
      </div>
    </div>
  );
}
